import {ALLOWED_DOMAINS, FORBIDDEN_DOMAINS} from '../../../../utils/constants'

// Accepts a bare domain (yourdomain.com) or a full URL with an optional
// protocol and a single trailing slash (https://yourdomain.com/).
export const DOMAIN_REGEX =
  /^(?:https?:\/\/)?[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}\/?$/

// Plans that render a quantity stepper (instead of a plain "Add to Order"
// link) in the Add-Ons grid. Add a plan code here to make it steppable.
// {min?: number, max?: number, step?: number, displayMultiplier?: number} —
// all optional, default to min: 0, max: unbounded, step: 1,
// displayMultiplier: 1. displayMultiplier scales only the number shown
// (quantity * displayMultiplier) — quantity itself (and price = unitPrice *
// quantity) is unaffected, for products billed by a "pack" of N units.
const PLAN_STEPPER = {
  'addon-socialadsbronze-5-m2m': {},
  'addon-socialadssilver-5-m2m': {},
  'addon-socialadsgold-5-m2m': {},
  'addon-5-ai-webbuilder': {},
  'addon-5-keap-addusers-m2m': {min: 1, max: 99},
  'addon-5-keap-addusers-annual': {min: 1, max: 99},
  'addon-5-keap-custdfy-ot': {min: 1, max: 999},
  // Professional Designed Logo — legacy options.js ITEM_CONFIG capped this
  // at 1-100 with a numeric input; a stepper is this grid's equivalent.
  thryvlogo4: {min: 1, max: 100},
  // 5 Extra Website Pages — legacy's SliderRange sold this in packs of 5
  // pages (max 250 pages = 50 packs, src/components/slider-range.js), priced
  // per pack (options.js: amount * quantity). quantity here is the pack
  // count (1-50); displayMultiplier shows the actual page count (5-250).
  thryv5page: {min: 1, max: 50, displayMultiplier: 5},
}

// Per-plan config for validate-input fields that send the user value as a URL
// path parameter. responseQuantityField: the response key whose value becomes
// the product quantity on "Add to Order". No DB schema change required.
const PATH_PARAM_VALIDATE_CONFIG = {
  'tl-mc-5-thryv-m2m': {responseQuantityField: 'budget'},
  'tl-tax-5-thryv-m2m': {responseQuantityField: 'budget'},
}

export function getFieldsFromPlan(plan) {
  if (!plan || !plan.fields?.length) return []
  const pathParamConfig = PATH_PARAM_VALIDATE_CONFIG[plan.code] ?? null
  return plan.fields.map(f => {
    const validators = f.validators ?? []
    // If the field type is 'domain', ensure the 'domain' format+TLD validator
    // is applied even when the API omits it from the validators array.
    const enrichedValidators =
      f.fieldType === 'domain' && !validators.includes('domain')
        ? [...validators, 'domain']
        : validators
    const needsRequired =
      f.isRequired && !enrichedValidators.includes('required')
    return {
      key: f.fieldKey,
      label: f.fieldLabel,
      type: f.fieldType,
      placeholder: f.placeholder,
      default: f.default,
      validators: needsRequired
        ? [...enrichedValidators, 'required']
        : enrichedValidators,
      isRequired: f.isRequired,
      validateEndpoint: f.validatorEndpoint,
      validateAsPathParam: pathParamConfig !== null,
      responseQuantityField: pathParamConfig?.responseQuantityField ?? null,
    }
  })
}

export function getStepperConfig(planCode) {
  return PLAN_STEPPER[planCode] ?? null
}

export function getDefaultValues(fields) {
  return fields.reduce((acc, field) => {
    acc[field.key] = field.default ?? ''
    return acc
  }, {})
}

export function validatePlanFields(fields, values, {validExtensionList} = {}) {
  const errors = {}
  for (const field of fields) {
    const value = values[field.key] ?? ''

    if (field.isRequired && !String(value).trim()) {
      errors[field.key] = `${field.label} is required`
      continue
    }

    if (!field.validators?.length) continue

    if (!String(value).trim() && !field.validators.includes('required'))
      continue

    for (const validator of field.validators) {
      if (validator === 'required') {
        if (!String(value).trim()) {
          errors[field.key] = `${field.label} is required`
          break
        }
      }
      if (validator === 'email') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
          errors[field.key] = 'Please enter a valid email address'
          break
        }
      }
      if (validator === 'domain') {
        const trimmed = String(value).trim().toLowerCase()
        let domainError = null
        if (!DOMAIN_REGEX.test(trimmed)) {
          domainError = 'Please enter a valid domain (e.g. yourdomain.com)'
        } else {
          const normalized = trimmed
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .split(/[/?#]/)[0]
          const isForbidden = FORBIDDEN_DOMAINS.some(
            d =>
              normalized === `${d}.com` ||
              normalized.includes(`.${d}.`) ||
              normalized.startsWith(`${d}.`) ||
              normalized.endsWith(`.${d}.com`),
          )
          if (isForbidden) {
            domainError = `The domain "${trimmed}" is not allowed`
          } else if (!ALLOWED_DOMAINS.some(d => trimmed.includes(d))) {
            // ALLOWED_DOMAINS (e.g. yelp.com) bypass the TLD check
            if (validExtensionList?.length) {
              const tld = normalized.split('.').pop()
              if (!validExtensionList.includes(tld)) {
                domainError = `"${tld}" is not a recognized domain extension`
              }
            }
          }
        }
        if (domainError) {
          errors[field.key] = domainError
          break
        }
      }
    }
  }
  return errors
}
